import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { dbOperation } from '../../utils/database';

const { FiUpload, FiImage, FiX, FiCheck, FiLoader } = FiIcons;

const ImageUploader = ({ 
  onUpload, 
  currentImage, 
  label = "Upload Image", 
  multiple = false, 
  acceptAll = false,
  resetTrigger = 0
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewImage, setPreviewImage] = useState('');
  const fileInputRef = useRef(null);
  const prevResetTrigger = useRef(resetTrigger);

  // Single useEffect to handle all image state updates
  useEffect(() => {
    console.log('ImageUploader: State update - resetTrigger:', resetTrigger, 'currentImage:', currentImage);
    
    // Check if reset was triggered
    if (resetTrigger !== prevResetTrigger.current) {
      console.log('ImageUploader: Reset triggered, clearing all state');
      
      // Complete reset
      setPreviewImage(currentImage || '');
      setIsUploading(false);
      setUploadProgress(0);
      setDragActive(false);
      
      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      // Update the previous trigger reference
      prevResetTrigger.current = resetTrigger;
    } else {
      // Normal image update (not a reset)
      console.log('ImageUploader: Normal image update');
      setPreviewImage(currentImage || '');
    }
  }, [resetTrigger, currentImage]);

  const handleFileSelect = (files) => {
    if (files && files.length > 0) {
      Array.from(files).forEach(file => {
        processFile(file);
      });
    }
  };

  const processFile = async (file) => {
    console.log('Processing file:', file.name);
    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 100);

      // Upload file using the database operation
      const result = await dbOperation.uploadFile(file, 'images');
      clearInterval(progressInterval);
      setUploadProgress(100);

      if (result.success) {
        console.log('File uploaded successfully:', result.url);

        // Save file metadata to database
        const fileMetadata = {
          id: Date.now().toString(),
          name: file.name,
          url: result.url,
          path: result.path,
          type: file.type.startsWith('image/') ? 'image' : 
                file.type.startsWith('video/') ? 'video' : 'document',
          size: file.size,
          uploaded_at: new Date().toISOString(),
          used_in: []
        };

        await dbOperation.saveMediaFile(fileMetadata);

        // Update preview immediately
        setPreviewImage(result.url);

        // Call the onUpload callback
        onUpload(result.url, file.name, fileMetadata.type);

        // Clear progress after a short delay
        setTimeout(() => {
          setUploadProgress(0);
        }, 1000);
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('File upload error:', error);
      alert('Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const removeImage = () => {
    console.log('Removing image...');
    setPreviewImage('');
    onUpload(''); // Clear the image in the parent component
    // Clear the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUrlInput = (url) => {
    if (url && url.trim()) {
      console.log('Adding image from URL:', url);
      setPreviewImage(url);
      onUpload(url);
    }
  };

  const acceptTypes = acceptAll ? "*/*" : "image/*,video/*";

  return (
    <div className="space-y-4">
      {/* Current Image Preview */}
      {previewImage && !multiple && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="relative inline-block"
        >
          <img
            src={previewImage}
            alt="Current"
            className="w-32 h-32 object-cover rounded-xl border border-gray-300 shadow-sm"
            onError={(e) => {
              console.log('Image failed to load:', previewImage);
              e.target.style.display = 'none';
            }}
          />
          <motion.button
            type="button"
            onClick={removeImage}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="absolute -top-2 -right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors shadow-sm"
          >
            <SafeIcon icon={FiX} className="h-3 w-3" />
          </motion.button>
        </motion.div>
      )}

      {/* Upload Area */}
      <motion.div
        className={`relative border-2 border-dashed rounded-2xl p-8 transition-all duration-300 ${
          dragActive 
            ? 'border-blue-400 bg-blue-50 scale-105' 
            : isUploading 
            ? 'border-purple-400 bg-purple-50' 
            : 'border-gray-300 hover:border-gray-400 bg-gray-50'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        whileHover={{ scale: 1.02 }}
        transition={{ type: "spring", stiffness: 300 }}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple={multiple}
          accept={acceptTypes}
          onChange={(e) => handleFileSelect(e.target.files)}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          disabled={isUploading}
        />
        
        <div className="text-center">
          {isUploading ? (
            <motion.div
              className="flex flex-col items-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <div className="relative mb-4">
                <SafeIcon icon={FiLoader} className="h-8 w-8 text-purple-600 animate-spin" />
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full animate-pulse opacity-20"></div>
              </div>
              <p className="text-sm font-medium text-gray-900 mb-2">Uploading...</p>
              <div className="w-full max-w-xs bg-gray-300 rounded-full h-2 overflow-hidden">
                <motion.div
                  className="bg-gradient-to-r from-blue-500 to-purple-500 h-full rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${uploadProgress}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
              <p className="text-xs text-gray-600 mt-1">{uploadProgress}%</p>
            </motion.div>
          ) : (
            <motion.div
              className="flex flex-col items-center"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center mb-4 shadow-sm">
                <SafeIcon icon={FiUpload} className="h-8 w-8 text-white" />
              </div>
              <p className="text-sm font-medium text-gray-900 mb-2">{label}</p>
              <p className="text-xs text-gray-600 text-center">
                Drag and drop or click to browse
                {acceptAll && (
                  <span className="block mt-1">
                    Supports images, videos, and documents
                  </span>
                )}
              </p>
            </motion.div>
          )}
        </div>
      </motion.div>

      {/* Quick Upload Buttons */}
      <div className="flex flex-col sm:flex-row gap-3">
        <motion.button
          type="button"
          onClick={handleClick}
          disabled={isUploading}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <SafeIcon icon={FiImage} className="h-4 w-4" />
          <span>Browse Files</span>
        </motion.button>

        {/* URL Input for quick image adding */}
        <div className="flex-1 flex gap-2">
          <input
            type="url"
            placeholder="Or paste image URL"
            className="flex-1 px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all duration-300 text-sm"
            onKeyPress={(e) => {
              if (e.key === 'Enter' && e.target.value) {
                handleUrlInput(e.target.value);
                e.target.value = '';
              }
            }}
            disabled={isUploading}
          />
          <motion.button
            type="button"
            onClick={(e) => {
              const input = e.target.previousElementSibling;
              if (input.value) {
                handleUrlInput(input.value);
                input.value = '';
              }
            }}
            disabled={isUploading}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-4 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-xl transition-all duration-300 disabled:opacity-50"
          >
            <SafeIcon icon={FiCheck} className="h-4 w-4" />
          </motion.button>
        </div>
      </div>
    </div>
  );
};

export default ImageUploader;